import React, { useState, useEffect, useMemo } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { notify } from '../CustomToast';
import calendarService from '../../services/calendarService';
import calendarTaskService from '../../services/calendarTaskService';
import ViewMeetingModal from '../Meetings/ViewMeetingModal';
import ScheduleMeetingModal from '../Meetings/ScheduleMeetingLandingModal';
import CreateTaskModal from './CreateTaskModal';
import TaskDetailsModal from './TaskDetailsModal';
import BackButton from '../Common/BackButton';
import { useDashboard } from '../../context/DashboardContext';
import CalendarSkeleton from './CalendarSkeleton';
import { useI18n } from '../../i18n';
import './CalendarSection.css';

const CalendarSection = ({ onMeetingJoin, activeMeeting }) => {
  const { t } = useI18n();
  const { setActiveView } = useDashboard();

  const currentUserId = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u?.id || null;
    } catch (e) {
      return null;
    }
  }, []);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [meetings, setMeetings] = useState({});
  const [tasks, setTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewType, setViewType] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return 'day';
    }
    return 'month';
  });
  const [showMoreDate, setShowMoreDate] = useState(null);

  // Format HH:MM from ISO string
  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const MAX_OVERLAP_COLS = 2;

  // Overlap layout capped at MAX_OVERLAP_COLS visible per group.
  // Returns { visible: [...items with _col/_cols], overflows: [{top, bottom, count, items}] }
  const computeOverlapLayout = (items) => {
    if (!items.length) return { visible: [], overflows: [] };
    const sorted = [...items].sort((a, b) => a._top - b._top);

    // Build overlap groups
    const groups = [];
    for (const item of sorted) {
      let placed = false;
      for (const group of groups) {
        if (group.some(g => g._top < item._bottom && item._top < g._bottom)) {
          group.push(item);
          placed = true;
          break;
        }
      }
      if (!placed) groups.push([item]);
    }

    const visible = [];
    const overflows = [];

    for (const group of groups) {
      const shown = group.slice(0, MAX_OVERLAP_COLS);
      const hidden = group.slice(MAX_OVERLAP_COLS);

      shown.forEach((item, i) => {
        item._col  = i;
        item._cols = shown.length;
        visible.push(item);
      });

      if (hidden.length > 0) {
        const top    = Math.min(...group.map(g => g._top));
        const bottom = Math.max(...group.map(g => g._bottom));
        overflows.push({ top, bottom, count: hidden.length, items: group });
      }
    }

    return { visible, overflows };
  };

  useEffect(() => {
    const controller = new AbortController();

    const run = async () => {
      try {
        setLoading(true);
        const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

        const [meetingResponse, taskData] = await Promise.all([
          calendarService.getMonthMeetings(month, controller.signal),
          calendarTaskService.getRangeTasks(
            `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-01`,
            `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate()).padStart(2, '0')}`,
            controller.signal
          ),
        ]);

        if (meetingResponse.status) {
          setMeetings(meetingResponse.data.meetings || {});
        } else {
          throw new Error(
            meetingResponse.message || t('pages.calendar.errors.load_failed')
          );
        }
        setTasks(taskData || {});
        setLoading(false);
      } catch (err) {
        if (err.code === 'ERR_CANCELED') return;
        setLoading(false);
        notify('error', err.message || t('pages.calendar.errors.load_failed'));
      }
    };

    run();
    return () => controller.abort();
  }, [currentDate, refreshKey, taskRefreshKey]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const handlePrev = () => {
    if (viewType === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    } else if (viewType === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 1);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    }
  };

  const handleNext = () => {
    if (viewType === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    } else if (viewType === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 1);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getHeaderTitle = () => {
    if (viewType === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      const startMonth = t(`common.months.${startOfWeek.getMonth()}`);
      const endMonth = t(`common.months.${endOfWeek.getMonth()}`);
      const startYear = startOfWeek.getFullYear();
      const endYear = endOfWeek.getFullYear();

      if (startMonth === endMonth && startYear === endYear) {
        return `${startMonth} ${startYear}`;
      } else if (startYear === endYear) {
        return `${startMonth} – ${endMonth} ${startYear}`;
      } else {
        return `${startMonth} ${startYear} – ${endMonth} ${endYear}`;
      }
    } else if (viewType === 'day') {
      const monthStr = t(`common.months.${currentDate.getMonth()}`);
      return `${monthStr} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
    }
    return `${t(`common.months.${currentDate.getMonth()}`)} ${currentDate.getFullYear()}`;
  };

  const handleMeetingClick = (meeting, e) => {
    e.stopPropagation();
    setSelectedMeeting(meeting);
    setShowMoreDate(null);
  };

  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr);
  };

  const handleMeetingCreated = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleTaskClick = (task, e) => {
    e.stopPropagation();
    setSelectedTask(task);
    setShowTaskDetails(true);
  };


  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const prevMonthDays = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
    const days = [];
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

    // Days from previous month
    for (let i = firstDay - 1; i >= 0; i--) {
      const dayNum = prevMonthDays - i;
      const idx = days.length;
      days.push(
        <div 
          key={`prev-${dayNum}`} 
          className="calendar-day other-month"
          onClick={handlePrev}
        >
          <div className="day-number">{dayNum}</div>
        </div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayMeetings = meetings[dateStr] || [];
      const isToday = dateStr === todayStr;
      const isSelected = dateStr === selectedDate;

      const idx = days.length;
      const dayTasks = tasks[dateStr] || [];
      const totalItems = dayMeetings.length + dayTasks.length;
      const visibleMeetings = dayMeetings.slice(0, 2);
      const remainingSlots = Math.max(0, 2 - visibleMeetings.length);
      const visibleTasks = dayTasks.slice(0, remainingSlots);
      const overflowCount = totalItems - visibleMeetings.length - visibleTasks.length;

      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''} ${isSelected && !isToday ? 'selected' : ''}`}
          onClick={() => handleDateClick(dateStr)}
        >
          <div className="day-number">
            {isToday ? <span className="today-circle">{day}</span> : day}
          </div>
          <div className="day-meetings">
            {visibleMeetings.map((meeting) => (
              <div
                key={meeting.id}
                className={`meeting-badge ${meeting.meeting_type}`}
                onClick={(e) => handleMeetingClick(meeting, e)}
                title={meeting.title}
              >
                {meeting.title.length > 15
                  ? `${meeting.title.substring(0, 15)}...`
                  : meeting.title}
              </div>
            ))}
            {visibleTasks.map((task) => (
              <div
                key={`task-${task.id}-${task.occurrence_date || task.start_at}`}
                className="meeting-badge task"
                onClick={(e) => handleTaskClick(task, e)}
                title={task.title}
              >
                {task.title.length > 15 ? task.title.substring(0, 15) + '...' : task.title}
              </div>
            ))}
          </div>
          {overflowCount > 0 && (
            <button
              className="day-overflow-btn"
              onClick={(e) => { e.stopPropagation(); setShowMoreDate(dateStr); }}
              title={`+${overflowCount} more`}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 4.5L6 8L10 4.5" stroke="#0C5097" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {showMoreDate === dateStr && (
            <>
              <div
                className="more-meetings-backdrop"
                onClick={(e) => { e.stopPropagation(); setShowMoreDate(null); }}
              />
              <div className="more-meetings-popover">
                <div className="more-meetings-header">
                  <div style={{width: 20}} />
                  <div className="more-meetings-header-center">
                    <span className="more-meetings-month-label">
                      {new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
                    </span>
                    <div className="more-meetings-day-circle">{day}</div>
                  </div>
                  <button
                    className="more-meetings-close"
                    onClick={(e) => { e.stopPropagation(); setShowMoreDate(null); }}
                    aria-label="Close"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 5L5 15M5 5L15 15" stroke="#49454F" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
                <div className="more-meetings-list">
                  {dayMeetings.map(meeting => (
                    <div
                      key={meeting.id}
                      className={`meeting-badge ${meeting.meeting_type}`}
                      onClick={(e) => handleMeetingClick(meeting, e)}
                      title={meeting.title}
                    >
                      {meeting.title.length > 25 ? `${meeting.title.substring(0, 25)}...` : meeting.title}
                    </div>
                  ))}
                  {dayTasks.map(task => (
                    <div
                      key={`task-${task.id}-${task.occurrence_date || task.start_at}`}
                      className="meeting-badge task"
                      onClick={(e) => handleTaskClick(task, e)}
                      title={task.title}
                    >
                      {task.title.length > 25 ? task.title.substring(0, 25) + '...' : task.title}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      );
    }

    // Days from next month
    const totalCells = firstDay + daysInMonth;
    const remainingCells = (7 - (totalCells % 7)) % 7;
    for (let i = 1; i <= remainingCells; i++) {
      const idx = days.length;
      days.push(
        <div 
          key={`next-${i}`} 
          className="calendar-day other-month"
          onClick={handleNext}
        >
          <div className="day-number">{i}</div>
        </div>
      );
    }

    return days;
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      weekDays.push(d);
    }

    const todayStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;

    const hours = [];
    for (let i = 0; i < 24; i++) {
      const ampm = i >= 12 ? 'PM' : 'AM';
      const hour12 = i % 12 || 12;
      hours.push(`${hour12} ${ampm}`);
    }

    return (
      <div className="calendar-week-view">
        <div className="calendar-week-header-row">
          <div className="calendar-time-col-header">
            <span className="calendar-timezone-label">GMT</span>
          </div>
          {weekDays.map((date, idx) => {
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const isToday = dateStr === todayStr;
            const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
            return (
              <div key={idx} className={`calendar-week-header-cell ${isToday ? 'today' : ''}`} onClick={() => handleDateClick(dateStr)}>
                <div className="calendar-week-day-name">{t(`pages.calendar.weekdays.${weekdayKeys[date.getDay()]}`)}</div>
                <div className="calendar-week-day-num">{date.getDate()}</div>
              </div>
            );
          })}
        </div>
        <div className="calendar-week-grid-scroll">
          <div className="calendar-week-grid">
            <div className="calendar-time-col">
              {hours.map((hour, idx) => (
                <div key={idx} className="calendar-time-slot">
                  <span>{hour}</span>
                </div>
              ))}
            </div>
            <div className="calendar-days-col">
              <div className="calendar-week-grid-lines">
                {hours.map((_, idx) => (
                  <div key={idx} className="calendar-grid-row-line"></div>
                ))}
              </div>
              <div className="calendar-week-day-cols">
                {weekDays.map((date, idx) => {
                  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const dayMeetings = meetings[dateStr] || [];
                  const dayTasks = tasks[dateStr] || [];
                  const rawItems = [
                    ...dayMeetings.map(m => ({ ...m, _type: 'meeting' })),
                    ...dayTasks.map(t => ({ ...t, _type: 'task' })),
                  ].map(item => {
                    const s = item.start_at ? new Date(item.start_at) : null;
                    const e = item.end_at   ? new Date(item.end_at)   : null;
                    const top    = s ? (s.getHours() * 60 + s.getMinutes()) : 0;
                    const bottom = e ? (e.getHours() * 60 + e.getMinutes()) : top + 60;
                    return { ...item, _top: top, _bottom: Math.max(bottom, top + 20) };
                  });

                  const { visible: layoutItems, overflows } = computeOverlapLayout(rawItems);

                  return (
                    <div key={idx} className="calendar-week-day-col">
                      {layoutItems.map(item => {
                        const colW = 100 / item._cols;
                        const left = item._col * colW;
                        const cls = item._type === 'task' ? 'week-event-pill task' : `week-event-pill ${item.meeting_type}`;
                        const onClick = item._type === 'task'
                          ? (e) => handleTaskClick(item, e)
                          : (e) => handleMeetingClick(item, e);
                        const key = item._type === 'task' ? `task-${item.id}` : item.id;
                        const timeLabel = item.start_at
                          ? fmtTime(item.start_at) + (item.end_at ? ` – ${fmtTime(item.end_at)}` : '')
                          : '';
                        return (
                          <div
                            key={key}
                            className={cls}
                            style={{
                              top:    `${item._top}px`,
                              height: `${item._bottom - item._top}px`,
                              left:   `${left + 1}%`,
                              width:  `${colW - 1.5}%`,
                            }}
                            onClick={onClick}
                            title={item.title}
                          >
                            <div className="week-event-title">{item.title.length > 18 ? item.title.substring(0, 18) + '…' : item.title}</div>
                            {(item._bottom - item._top) >= 36 && timeLabel && (
                              <div className="week-event-time">{timeLabel}</div>
                            )}
                          </div>
                        );
                      })}
                      {overflows.map((ov, oi) => (
                        <div
                          key={`ov-${oi}`}
                          className="week-overflow-btn"
                          style={{ top: `${ov.bottom - 18}px`, right: '4px' }}
                          onClick={(e) => { e.stopPropagation(); setShowMoreDate(`${dateStr}-ov-${oi}`); }}
                          title={`+${ov.count} more`}
                        >
                          +{ov.count}
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 4.5L6 8L10 4.5" stroke="#0C5097" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </div>
                      ))}
                      {overflows.map((ov, oi) => showMoreDate === `${dateStr}-ov-${oi}` && (
                        <React.Fragment key={`pop-${oi}`}>
                          <div className="more-meetings-backdrop" onClick={(e) => { e.stopPropagation(); setShowMoreDate(null); }} />
                          <div className="more-meetings-popover" style={{ top: `${ov.bottom}px` }}>
                            <div className="more-meetings-header">
                              <div style={{ width: 20 }} />
                              <div className="more-meetings-header-center">
                                <span className="more-meetings-month-label">{date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}</span>
                                <div className="more-meetings-day-circle">{date.getDate()}</div>
                              </div>
                              <button className="more-meetings-close" onClick={(e) => { e.stopPropagation(); setShowMoreDate(null); }}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="#49454F" strokeWidth="1.5" strokeLinecap="round"/></svg>
                              </button>
                            </div>
                            <div className="more-meetings-list">
                              {ov.items.map(item => item._type === 'task' ? (
                                <div key={`task-${item.id}`} className="meeting-badge task" onClick={(e) => handleTaskClick(item, e)} title={item.title}>
                                  {item.title.length > 25 ? item.title.substring(0, 25) + '…' : item.title}
                                </div>
                              ) : (
                                <div key={item.id} className={`meeting-badge ${item.meeting_type}`} onClick={(e) => handleMeetingClick(item, e)} title={item.title}>
                                  {item.title.length > 25 ? item.title.substring(0, 25) + '…' : item.title}
                                </div>
                              ))}
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const todayStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    const isToday = dateStr === todayStr;
    const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    
    const dayMeetings = meetings[dateStr] || [];
    const dayTasks = tasks[dateStr] || [];

    const hours = [];
    for (let i = 0; i < 24; i++) {
      const ampm = i >= 12 ? 'PM' : 'AM';
      const hour12 = i % 12 || 12;
      hours.push(`${hour12} ${ampm}`);
    }

    return (
      <div className="calendar-week-view">
        <div className="calendar-week-header-row">
          <div className="calendar-time-col-header">
            <span className="calendar-timezone-label">GMT</span>
          </div>
          <div className={`calendar-week-header-cell ${isToday ? 'today' : ''}`} onClick={() => handleDateClick(dateStr)}>
            <div className="calendar-week-day-name">{t(`pages.calendar.weekdays.${weekdayKeys[currentDate.getDay()]}`)}</div>
            <div className="calendar-week-day-num">{currentDate.getDate()}</div>
          </div>
        </div>
        <div className="calendar-week-grid-scroll">
          <div className="calendar-week-grid">
            <div className="calendar-time-col">
              {hours.map((hour, idx) => (
                <div key={idx} className="calendar-time-slot">
                  <span>{hour}</span>
                </div>
              ))}
            </div>
            <div className="calendar-days-col">
              <div className="calendar-week-grid-lines">
                {hours.map((_, idx) => (
                  <div key={idx} className="calendar-grid-row-line"></div>
                ))}
              </div>
              <div className="calendar-week-day-cols">
                <div className="calendar-week-day-col">
                  {(() => {
                    const rawItems = [
                      ...dayMeetings.map(m => ({ ...m, _type: 'meeting' })),
                      ...dayTasks.map(t => ({ ...t, _type: 'task' })),
                    ].map(item => {
                      const s = item.start_at ? new Date(item.start_at) : null;
                      const e = item.end_at   ? new Date(item.end_at)   : null;
                      const top    = s ? (s.getHours() * 60 + s.getMinutes()) : 0;
                      const bottom = e ? (e.getHours() * 60 + e.getMinutes()) : top + 60;
                      return { ...item, _top: top, _bottom: Math.max(bottom, top + 20) };
                    });
                    const { visible: layoutItems, overflows } = computeOverlapLayout(rawItems);
                    return (
                      <>
                        {layoutItems.map(item => {
                          const colW = 100 / item._cols;
                          const cls = item._type === 'task' ? 'week-event-pill task' : `week-event-pill ${item.meeting_type}`;
                          const onClick = item._type === 'task' ? (e) => handleTaskClick(item, e) : (e) => handleMeetingClick(item, e);
                          const key = item._type === 'task' ? `task-${item.id}` : item.id;
                          const timeLabel = item.start_at ? fmtTime(item.start_at) + (item.end_at ? ` – ${fmtTime(item.end_at)}` : '') : '';
                          return (
                            <div key={key} className={cls}
                              style={{ top: `${item._top}px`, height: `${item._bottom - item._top}px`, left: `${item._col * colW + 1}%`, width: `${colW - 1.5}%` }}
                              onClick={onClick} title={item.title}>
                              <div className="week-event-title">{item.title.length > 28 ? item.title.substring(0, 28) + '…' : item.title}</div>
                              {(item._bottom - item._top) >= 36 && timeLabel && <div className="week-event-time">{timeLabel}</div>}
                            </div>
                          );
                        })}
                        {overflows.map((ov, oi) => (
                          <div key={`ov-${oi}`} className="week-overflow-btn"
                            style={{ top: `${ov.bottom - 18}px`, right: '4px' }}
                            onClick={(e) => { e.stopPropagation(); setShowMoreDate(`day-ov-${oi}`); }}
                            title={`+${ov.count} more`}>
                            +{ov.count}
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 4.5L6 8L10 4.5" stroke="#0C5097" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                        ))}
                        {overflows.map((ov, oi) => showMoreDate === `day-ov-${oi}` && (
                          <React.Fragment key={`pop-${oi}`}>
                            <div className="more-meetings-backdrop" onClick={(e) => { e.stopPropagation(); setShowMoreDate(null); }} />
                            <div className="more-meetings-popover" style={{ top: `${ov.bottom}px` }}>
                              <div className="more-meetings-header">
                                <div style={{ width: 20 }} />
                                <div className="more-meetings-header-center">
                                  <span className="more-meetings-month-label">{currentDate.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}</span>
                                  <div className="more-meetings-day-circle">{currentDate.getDate()}</div>
                                </div>
                                <button className="more-meetings-close" onClick={(e) => { e.stopPropagation(); setShowMoreDate(null); }}>
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="#49454F" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                </button>
                              </div>
                              <div className="more-meetings-list">
                                {ov.items.map(item => item._type === 'task' ? (
                                  <div key={`task-${item.id}`} className="meeting-badge task" onClick={(e) => handleTaskClick(item, e)} title={item.title}>
                                    {item.title.length > 25 ? item.title.substring(0, 25) + '…' : item.title}
                                  </div>
                                ) : (
                                  <div key={item.id} className={`meeting-badge ${item.meeting_type}`} onClick={(e) => handleMeetingClick(item, e)} title={item.title}>
                                    {item.title.length > 25 ? item.title.substring(0, 25) + '…' : item.title}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="calendar-section">
      <div className="calendar-section-header">
        <div className="dashboard-section-header">
          <h1 className="dashboard-section-title">
            <BackButton
              onClick={() => setActiveView('dashboard')}
              ariaLabel={t('common.navigation.back_to_dashboard')}
            />
            {t('pages.calendar.title')}
          </h1>
        </div>
        <div className="calendar-header-actions">
          <button
            className="calendar-create-task-btn"
            onClick={() => setShowCreateTaskModal(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.25 7.99864C14.25 8.19756 14.171 8.38832 14.0303 8.52897C13.8896 8.66963 13.6989 8.74864 13.5 8.74864H7.99997C7.80106 8.74864 7.61029 8.66963 7.46964 8.52897C7.32899 8.38832 7.24997 8.19756 7.24997 7.99864C7.24997 7.79973 7.32899 7.60897 7.46964 7.46831C7.61029 7.32766 7.80106 7.24864 7.99997 7.24864H13.5C13.6989 7.24864 13.8896 7.32766 14.0303 7.46831C14.171 7.60897 14.25 7.79973 14.25 7.99864ZM7.99997 4.74864H13.5C13.6989 4.74864 13.8896 4.66963 14.0303 4.52897C14.171 4.38832 14.25 4.19756 14.25 3.99864C14.25 3.79973 14.171 3.60897 14.0303 3.46831C13.8896 3.32766 13.6989 3.24864 13.5 3.24864H7.99997C7.80106 3.24864 7.61029 3.32766 7.46964 3.46831C7.32899 3.60897 7.24997 3.79973 7.24997 3.99864C7.24997 4.19756 7.32899 4.38832 7.46964 4.52897C7.61029 4.66963 7.80106 4.74864 7.99997 4.74864ZM13.5 11.2486H7.99997C7.80106 11.2486 7.61029 11.3277 7.46964 11.4683C7.32899 11.609 7.24997 11.7997 7.24997 11.9986C7.24997 12.1976 7.32899 12.3883 7.46964 12.529C7.61029 12.6696 7.80106 12.7486 7.99997 12.7486H13.5C13.6989 12.7486 13.8896 12.6696 14.0303 12.529C14.171 12.3883 14.25 12.1976 14.25 11.9986C14.25 11.7997 14.171 11.609 14.0303 11.4683C13.8896 11.3277 13.6989 11.2486 13.5 11.2486ZM4.96935 2.46802L3.49997 3.93614L3.0306 3.46614C2.8897 3.32525 2.69861 3.24609 2.49935 3.24609C2.30009 3.24609 2.10899 3.32525 1.9681 3.46614C1.8272 3.60704 1.74805 3.79814 1.74805 3.99739C1.74805 4.19665 1.8272 4.38775 1.9681 4.52864L2.9681 5.52864C3.03778 5.59856 3.12057 5.65404 3.21173 5.6919C3.3029 5.72975 3.40064 5.74924 3.49935 5.74924C3.59806 5.74924 3.6958 5.72975 3.78696 5.6919C3.87813 5.65404 3.96092 5.59856 4.0306 5.52864L6.0306 3.52864C6.17149 3.38775 6.25065 3.19665 6.25065 2.99739C6.25065 2.79814 6.17149 2.60704 6.0306 2.46614C5.8897 2.32525 5.69861 2.24609 5.49935 2.24609C5.30009 2.24609 5.10899 2.32525 4.9681 2.46614L4.96935 2.46802ZM4.96935 6.46802L3.49997 7.93614L3.0306 7.46614C2.8897 7.32525 2.69861 7.24609 2.49935 7.24609C2.30009 7.24609 2.10899 7.32525 1.9681 7.46614C1.8272 7.60704 1.74805 7.79814 1.74805 7.99739C1.74805 8.19665 1.8272 8.38775 1.9681 8.52864L2.9681 9.52864C3.03778 9.59856 3.12057 9.65404 3.21173 9.6919C3.3029 9.72975 3.40064 9.74924 3.49935 9.74924C3.59806 9.74924 3.6958 9.72975 3.78696 9.6919C3.87813 9.65404 3.96092 9.59856 4.0306 9.52864L6.0306 7.52864C6.17149 7.38775 6.25065 7.19665 6.25065 6.99739C6.25065 6.79814 6.17149 6.60704 6.0306 6.46614C5.8897 6.32525 5.69861 6.24609 5.49935 6.24609C5.30009 6.24609 5.10899 6.32525 4.9681 6.46614L4.96935 6.46802ZM4.96935 10.468L3.49997 11.9361L3.0306 11.4661C2.96083 11.3964 2.87801 11.341 2.78686 11.3033C2.69571 11.2655 2.59801 11.2461 2.49935 11.2461C2.40069 11.2461 2.30299 11.2655 2.21184 11.3033C2.12069 11.341 2.03786 11.3964 1.9681 11.4661C1.89833 11.5359 1.84299 11.6187 1.80524 11.7099C1.76748 11.801 1.74805 11.8987 1.74805 11.9974C1.74805 12.0961 1.76748 12.1938 1.80524 12.2849C1.84299 12.3761 1.89833 12.4589 1.9681 12.5286L2.9681 13.5286C3.03778 13.5986 3.12057 13.654 3.21173 13.6919C3.3029 13.7298 3.40064 13.7492 3.49935 13.7492C3.59806 13.7492 3.6958 13.7298 3.78696 13.6919C3.87813 13.654 3.96092 13.5986 4.0306 13.5286L6.0306 11.5286C6.17149 11.3877 6.25065 11.1967 6.25065 10.9974C6.25065 10.7981 6.17149 10.607 6.0306 10.4661C5.8897 10.3252 5.69861 10.2461 5.49935 10.2461C5.30009 10.2461 5.10899 10.3252 4.9681 10.4661L4.96935 10.468Z" fill="white"/>
            </svg>
            {t('pages.calendar.create_task')}
          </button>
          <button
            className="calendar-schedule-btn"
            onClick={() => setShowCreateModal(true)}
          >
            {t('pages.calendar.schedule_meeting')}
          </button>
        </div>
      </div>

      {loading ? (
        <CalendarSkeleton />
      ) : (
        <div className="calendar-grid-wrapper">
          <div className="calendar-card">
          <div className="calendar-header-row">
            <div className="calendar-nav">
              <button className="calendar-today-btn" onClick={handleToday}>
                Today
              </button>
              <button className="calendar-nav-btn" onClick={handlePrev}>
                <FaChevronLeft />
              </button>
              <button className="calendar-nav-btn" onClick={handleNext}>
                <FaChevronRight />
              </button>
              <h3 className="calendar-month-title">
                {getHeaderTitle()}
              </h3>
            </div>

            <div className="calendar-view-selector">
              <select
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
                className="calendar-view-dropdown"
              >
                <option value="day">{t('pages.calendar.views.day')}</option>
                <option value="week">{t('pages.calendar.views.week')}</option>
                <option value="month">{t('pages.calendar.views.month')}</option>
              </select>
            </div>
          </div>

          {viewType === 'month' && (
            <>
              <div className="calendar-weekday-header">
                {['sun','mon','tue','wed','thu','fri','sat'].map(k => (
                  <div key={k} className="calendar-weekday-label">{t(`pages.calendar.weekdays.${k}`)}</div>
                ))}
              </div>
              <div className="calendar-grid">{renderCalendar()}</div>
            </>
          )}
          {viewType === 'week' && renderWeekView()}
          {viewType === 'day' && renderDayView()}
          </div>
        </div>
      )}

      {showCreateModal && (
        <ScheduleMeetingModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleMeetingCreated}
        />
      )}

      {selectedMeeting && (
        <ViewMeetingModal
          isOpen={true}
          meeting={selectedMeeting}
          onClose={() => setSelectedMeeting(null)}
          currentUserId={currentUserId}
          onUpdate={() => setRefreshKey((prev) => prev + 1)}
          onMeetingJoin={onMeetingJoin}
        />
      )}

      {showCreateTaskModal && (
        <CreateTaskModal
          isOpen={showCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
          onSuccess={() => {
            setShowCreateTaskModal(false);
            setTaskRefreshKey(k => k + 1);
          }}
        />
      )}

      {showTaskDetails && selectedTask && (
        <TaskDetailsModal
          isOpen={showTaskDetails}
          task={selectedTask}
          onClose={() => { setShowTaskDetails(false); setSelectedTask(null); }}
          onUpdated={(updatedTask) => {
            setSelectedTask(updatedTask);
            setTaskRefreshKey(k => k + 1);
          }}
          onDeleted={() => {
            setShowTaskDetails(false);
            setSelectedTask(null);
            setTaskRefreshKey(k => k + 1);
          }}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
};

export default CalendarSection;
